// Amazon-style ProductDetail with responsive Amazon-like layout + Skeleton + Trust Section
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { setCheckout } from "../utils/buyNowStorage";
import { getPolicyBadges } from "../utils/policyBadges";
import api from "../api/axios";
import ReviewDialog from "../components/reviews/ReviewDialog";
import ProductQnA from "../components/qna/ProductQnA";
import BestSellerCarousel from "./BestSellerCarousel";

// Import extracted components
import MediaGallery from "../components/ProductDetail/MediaGallery";
import ProductInfo from "../components/ProductDetail/ProductInfo";
import BuyBox from "../components/ProductDetail/BuyBox";
import RatingsReviews from "../components/ProductDetail/RatingsReviews";
import SpecsTable from "../components/ProductDetail/SpecsTable";
import BankOffers from "../components/ProductDetail/BankOffers";
import FrequentlyBoughtTogether from "../components/ProductDetail/FrequentlyBoughtTogether";
import SimilarProducts from "../components/ProductDetail/SimilarProducts";
import RecentlyViewed from "../components/ProductDetail/RecentlyViewed";
import ProductDetailSkeleton from "./ProductDetailSkeleton";
import { AuthContext } from "../context/AuthContext";

// import ScrollToTop from "./ScrollToTop";
/* ====================================================== */

  const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = React.useContext(AuthContext);
 
  // const product = reelsData.find((item) => String(item.id) === String(id));
  const { toggleWishlist, isWishlisted } = useWishlist();
  // const wishlisted = isWishlisted(product?.id);
  const [liked, setLiked] = useState(false);
  const { addToCart } = useCart();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [similarProducts, setSimilarProducts] = useState([]);
    const [ratingSummary, setRatingSummary] = useState(null);

    const [pincode, setPincode] = useState("");
    const [qty, setQty] = useState(1);
    const [deliveryEta, setDeliveryEta] = useState("");
    const [openEmi, setOpenEmi] = useState(false);
  // Reviews
const [openReviewDialog, setOpenReviewDialog] = useState(false);
const [editingReviewId, setEditingReviewId] = useState(null);
const [reviewSectionRefreshKey, setReviewSectionRefreshKey] = useState(0);

const [canReview, setCanReview] = useState(false);
const [reviewOrderId, setReviewOrderId] = useState(null);
const [recentlyViewed, setRecentlyViewed] = useState([]);

useEffect(() => {
  window.scrollTo(0, 0);
}, [id]);
  /* ======================
     LOAD PRODUCT (API)
     ====================== */
useEffect(() => {
  const loadProduct = async () => {
    setLoading(true); // 🔥 ADD THIS

    try {
      const res = await api.get(`/reels/${id}`);
      setProduct(res.data);
    } catch (err) {
      console.error("Failed to load product", err);
    } finally {
      setLoading(false);
    }
  };

  loadProduct();
}, [id]);

  /* Load rating summary */
  useEffect(() => {
    if (!product?._id) return;

    api
      .get(`/reviews/summary/${product._id}`)
      .then((res) => setRatingSummary(res.data))
      .catch(() => setRatingSummary(null));
  }, [product, reviewSectionRefreshKey]);

  /* Recently Viewed ====================== */
  useEffect(() => {
  if (!product?._id) return;

  let viewed = JSON.parse(localStorage.getItem("recentlyViewed")) || [];

  // remove duplicates
  viewed = viewed.filter((id) => id !== product._id);

  // add new product at beginning
  viewed.unshift(product._id);

  // keep max 20
  viewed = viewed.slice(0, 20);

  localStorage.setItem("recentlyViewed", JSON.stringify(viewed));

  // Sync to API for logged-in users
  if (user) {
    api.post("/browse-history/add", { productId: product._id }).catch(console.error);
  }
}, [product, user]);



useEffect(() => {
  const ids = JSON.parse(localStorage.getItem("recentlyViewed")) || [];

  if (!ids.length) return;

  api.post("/products/recently-viewed", { ids })
    .then((res) => setRecentlyViewed(res.data.products))
    .catch(console.error);

}, []);
  
const stock = product?.stock ?? 0;
  const inStock = stock > 0;
  const [activeMedia, setActiveMedia] = useState(null);

useEffect(() => {
  if (qty > stock) {
    setQty(stock > 0 ? 1 : 0);
  }
}, [stock]);
  /* ======================
     MEDIA SETUP
     ====================== */
 const mediaItems = useMemo(() => {
  if (!product) return [];
  const images = Array.isArray(product.images)
    ? product.images.map((img) => ({
        type: "image",
        src: img.thumbnailUrl || img.url, // thumbnail preferred
      }))
    : [];

  const videos = Array.isArray(product.videos)
    ? product.videos.map((vid) => ({
        type: "video",
        src: vid.url,
      }))
    : [];

  return [...images, ...videos];
}, [product]);


  useEffect(() => {
    if (mediaItems.length) {
      setActiveMedia(mediaItems[0]);
    }
  }, [mediaItems]);

  
  useEffect(() => {
  if (!product || !user) return; // skip eligibility check for unauthenticated users — avoids 401 redirect

  const checkEligibility = async () => {
    try {
      const res = await api.get(`/reviews/eligibility/${product._id}`);
      setCanReview(res.data.canReview);
      setReviewOrderId(res.data.orderId);
    } catch (err) {
      setCanReview(false);
    }
  };

  checkEligibility();
}, [product, user]);

    /* ======================
     LOAD SIMILAR PRODUCTS (API)
     ====================== */
useEffect(() => {
  if (!product) return;

  const loadSimilar = async () => {
    try {
      const res = await api.get(`/reels/${id}/similar`);
      setSimilarProducts(res.data.similar || []);
    } catch (err) {
      console.error("Failed to load similar products", err);
      setSimilarProducts([]);
    }
  };
  loadSimilar();
}, [product, id]);

useEffect(() => {
  if (location.state?.openReview && canReview) {
    setOpenReviewDialog(true);
  }
}, [location.state, canReview]);

  if (loading) return <ProductDetailSkeleton />;
  if (!product) {
    return <Typography sx={{ p: 4 }}>Product not found</Typography>;
  }

  const wishlisted = isWishlisted(product._id);
  const { title, description, price,videoUrl, imageUrl, vendorName, businessName, policies, vendor } = product;//inStock = true,
  
  // --- DEBUGGING: Check description content ---
  //console.log("Original description:", description);
  //console.log("Sanitized description:", sanitizeHtml(description));
  // ------------------------------------------
  
  const sanitizePrice = (p) => Number(String(p).replace(/,/g, "")) || 0;
const badges = getPolicyBadges(product.policies);
const handleBuyNow = () => {
  console.log("Initiating Buy Now for product:", product);
  const user = JSON.parse(localStorage.getItem("user"));
  
  // Ensure qty is at least 1
  const finalQty = qty > 0 ? qty : 1;
  
  setCheckout({
    mode: "BUY_NOW",
    items: [
      {
        id: product._id,
        title: product.title,
        price: sanitizePrice(product.price),
        imageUrl: product.images[0]?.thumbnailKey || product.images[0],
        qty: finalQty,
        vendorId: product.vendorId,
        vendorName: product.vendorName,
      },
    ],
  });
  if (!user) return navigate("/login");
  if (!user.profileCompleted) return navigate("/register/customer");
  navigate(`/delivery-location/${product._id}`);
};

  const handleShare = async () => {
    if (!product) return;
    
    const productUrl = window.location.href;
    
    // Create share text with product details
    const priceText = `₹${Number(product.price).toLocaleString("en-IN")}`;
    const ratingText = product.rating ? `⭐ ${product.rating.toFixed(1)}` : '';
    const shareText = `Check out "${product.title}" ${priceText} ${ratingText}`.trim();
    
    const shareData = {
      title: product.title,
      text: shareText,
      url: productUrl,
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Fallback to copying URL to clipboard with a message
          const message = `${shareText}\n\n${productUrl}`;
          navigator.clipboard.writeText(message)
            .then(() => alert('Product details copied to clipboard! Paste to share.'))
            .catch(() => alert(`Share this product:\n\n${message}`));
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      const message = `${shareText}\n\n${productUrl}`;
      navigator.clipboard.writeText(message)
        .then(() => alert('Product details copied to clipboard! Paste to share.'))
        .catch(() => {
          const textArea = document.createElement('textarea');
          textArea.value = message;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          alert('Product details copied to clipboard! Paste to share.');
        });
    }
  };
  /* ====================================================== */
const handleAddToCart = () => {
  if (!product ) return;

  const quantity = qty || 1;
  // Extract image URL from the first media item (may be an object or string)
  const firstImage = product.media?.find(m => m.type === "image") || product.images?.[0];
  const imageUrl = typeof firstImage === "string" ? firstImage
    : firstImage?.thumbnailUrl || firstImage?.url || "";

  addToCart({
    id: product._id,
    title: product.title,
    price: sanitizePrice(product.price),
    imageUrl,
    qty: quantity,
    vendorId: product.vendorId,
    vendorName: product.vendorName,
  });
};

  return (
    <Box sx={{ bgcolor: "#eaeded", minHeight: "100vh", pt: "64px", pb: 4 }}>
      <Grid container sx={{ maxWidth: 1300, mx: "auto", px: 2 }} spacing={3}>
        {/* ================= MEDIA + BUY BOX ================= */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "2.2fr 1fr" },
              gap: 3,
              alignItems: "flex-start",
            }}
          >
            {/* ================= LEFT: MEDIA + DETAILS ================= */}
            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <MediaGallery
                  mediaItems={mediaItems}
                  activeMedia={activeMedia}
                  setActiveMedia={setActiveMedia}
                />
                <ProductInfo
                  product={product}
                  wishlisted={wishlisted}
                  toggleWishlist={toggleWishlist}
                  liked={liked}
                  setLiked={setLiked}
                  handleShare={handleShare}
                  badges={badges}
                  ratingSummary={ratingSummary}
                />
              </CardContent>
            </Card>

            {/* ================= RIGHT: BUY BOX ================= */}
            <BuyBox
              price={price}
              inStock={inStock}
              stock={stock}
              pincode={pincode}
              setPincode={setPincode}
              deliveryEta={deliveryEta}
              setDeliveryEta={setDeliveryEta}
              qty={qty}
              setQty={setQty}
              handleAddToCart={handleAddToCart}
              handleBuyNow={handleBuyNow}
              product={product}
              badges={badges}
              navigate={navigate}
              businessName={businessName}
              vendor={vendor}
              vendorName={vendorName}
            />
          </Box>
        </Grid>

        {/* ================= TRUST SECTION ================= */}
        <Grid item xs={12}>
          <Box
            sx={{
              width: { xs: "100%", md: "68.75%" }, // matches left card width (2.2fr out of 3.2fr)
            }}
          >
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography fontWeight={700} mb={1}>
                Why shop with us?
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  🚚 Fast & Free Delivery
                </Grid>
                <Grid item xs={12} sm={6}>
                  💳 Secure Payments
                </Grid>
                <Grid item xs={12} sm={6}>
                  🔄 Easy Returns
                </Grid>
                <Grid item xs={12} sm={6}>
                  ⭐ Top Rated Sellers
                </Grid>
              </Grid>
            </Paper>
            <RatingsReviews
              productId={product._id}
              productVendorId={product.vendorId}
              canReview={canReview}
              onWriteReview={() => {
                setEditingReviewId(null);
                setOpenReviewDialog(true);
              }}
              onEditReview={(reviewId) => {
                setEditingReviewId(reviewId);
                setOpenReviewDialog(true);
              }}
              initialSummary={ratingSummary}
              refreshKey={reviewSectionRefreshKey}
            />
            <ProductQnA productId={product._id} productVendorId={product.vendorId} />
            <BankOffers />
            <Typography
              variant="caption"
              sx={{ mt: 1, color: "#007185", cursor: "pointer" }}
              onClick={() => setOpenEmi(true)}
            >
              No-cost EMI available ›
            </Typography>
            <ReviewDialog
              open={openReviewDialog}
              onClose={() => {
                setOpenReviewDialog(false);
                setEditingReviewId(null);
              }}
              productId={product._id}
              orderId={reviewOrderId}
              mode={editingReviewId ? "EDIT" : "WRITE"}
              reviewId={editingReviewId}
              onSuccess={() => setReviewSectionRefreshKey((prev) => prev + 1)}
            />

            <Dialog open={openEmi} onClose={() => setOpenEmi(false)} fullWidth>
              <DialogTitle>EMI Options</DialogTitle>
              <DialogContent>
                <Typography>₹499 / month for 12 months</Typography>
                <Typography>No-cost EMI on select banks</Typography>
                <Typography>Credit & Debit cards supported</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenEmi(false)}>Close</Button>
              </DialogActions>
            </Dialog>
            <FrequentlyBoughtTogether product={product} />
            <SpecsTable product={product} />
            <SimilarProducts products={similarProducts} navigate={navigate} />
            <BestSellerCarousel
              categoryId={product.category}
              navigate={navigate}
            />
            {/* <RecentlyViewed products={reelsData} navigate={navigate} /> */}
            <RecentlyViewed
              products={recentlyViewed}
              navigate={navigate}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductDetail;

